// Dev-only sample data. In a `vite dev` build with PUBLIC_MOCKS=1, `apiFetch`
// short-circuits to these fixtures instead of hitting the API — so every page
// renders populated content without a running backend or seeded DB. The
// `import.meta.env.DEV` guard dead-code-eliminates this out of prod bundles.
//
//   PUBLIC_MOCKS=1 bun run dev   # populated UI, no API needed
//
// THIS FILE IS THE REGISTRY AND THE THREE DOORS (two session, one latency), and
// nothing else. Every fixture lives in a `lib/<family>-mocks.ts` beside it (plan
// 101 slice 15 step 4 finished the split: `mockFetch` was a 523-line ladder with
// a cognitive complexity of 134, against a ceiling of 25). A new family gets a
// module and a registry entry — never a branch here.

import { sleep } from '@developerz/kit';

import { accountConnectionsMock } from './account-connections-mocks';
import { accountNotificationsMock } from './account-notifications-mocks';
import { accountSettingsMock } from './account-settings-mocks';
import { accountTokensMock } from './account-tokens-mocks';
import { accountWebhooksMock } from './account-webhooks-mocks';
import { adminAccessMock } from './admin-access-mocks';
import { adminConsoleMock } from './admin-console-mocks';
import { adminGoalsMock } from './admin-goals-mocks';
import { adminGrantsMock } from './admin-grants-mocks';
import { adminHiveMock } from './admin-hive-mocks';
import { adminOperatorMock } from './admin-operator-mocks';
import { agentActivityMock } from './agent-activity-mocks';
import { agentModesMock } from './agent-modes-mocks';
import { agentQueueMock } from './agent-queue-mocks';
import { agentsHistoryMock } from './agents-history-mocks';
import { agentsPreviewMock } from './agents-preview-mocks';
import { ApiError } from './api';
import { UserFacingError } from './api-errors';
import { approvalsMock } from './approvals-mocks';
import { artifactsMock } from './artifacts-mocks';
import { auditFeedMock } from './audit-feed-mocks';
import { billingMock } from './billing-mocks';
import { blockersMock } from './blockers-mocks';
import { boxLifecycleMock } from './box-lifecycle-mocks';
import { ciMock } from './ci-mocks';
import { dashboardMock } from './dashboard-mocks';
import { donationsMock } from './donations-mocks';
import { emailStatusMock } from './email-status-mocks';
import { fleetBoxesMock } from './fleet-boxes-mocks';
import { fleetLiveMock } from './fleet-live-mocks';
import { fleetMoveMock } from './fleet-move-mocks';
import { fleetTasksMock } from './fleet-tasks-mocks';
import { fleetUtilizationMock } from './fleet-utilization-mocks';
import { identitiesMock } from './identities-mocks';
import { kbMock } from './kb-mocks';
import { keyOutageMock } from './key-outage-mocks';
import { keysMock } from './keys-mocks';
import { llmRoutesMock } from './llm-routes-mocks';
import { machineHistoryMock } from './machine-history-mocks';
import { me, meInvited, meMember } from './mock-session';
import { notificationPageMock } from './notification-page-mocks';
import { notificationSettingsMock } from './notification-settings-mocks';
import { onboardingMock } from './onboarding-mocks';
import { invitationAcceptMock } from './org-invitations-mocks';
import { orgMock } from './org-mocks';
import { orgSsoMock } from './org-sso-mocks';
import { ossMock } from './oss-mocks';
import { overviewCountsMock } from './overview-counts-mocks';
import { plansMock } from './plans-mocks';
import { promptVarsMock } from './prompt-vars-mocks';
import { repoEnablementMock } from './repo-enablement-mocks';
import { repoIssueMock } from './repo-issue-mocks';
import { repoReadinessMock } from './repo-readiness-mocks';
import { repoWorkspaceMock } from './repo-workspace-mocks';
import { sessionsMock } from './sessions-mocks';
import { squadsMock } from './squads-mocks';
import { systemPromptsMock } from './system-prompts-mocks';
import { templatesMock } from './templates-mocks';
import { terminalMock } from './terminal-mocks';
import { unhandledMockRoute } from './unhandled-mock-route';

/** True only in a dev build with PUBLIC_MOCKS=1. Prod build → false → tree-shaken. */
export const MOCKS: boolean = import.meta.env.DEV && import.meta.env['PUBLIC_MOCKS'] === '1';

/**
 * One resource family's mock: answer the request, or `null` for "not my route".
 *
 * `null` — never `{}` — is the load-bearing half of the contract. This mock plane
 * is the ENTIRE backend for the Playwright lane, so a responder that answered an
 * empty object for an address it does not serve would turn a missing handler into
 * a screen that renders its empty state, and the story over it would pass. Seven
 * families reached prod with no handler at all and were found only because the
 * fallthrough THROWS; that is the property this type exists to keep.
 */
type MockResponder = (path: string, method: string, init?: RequestInit) => Promise<unknown> | null;

/**
 * Every resource family the mock plane serves, each owning one module.
 *
 * A registry rather than a ladder of `if`s: the list is a VALUE, so "which
 * families does the mock plane serve" is something a test can read
 * (`mock-coverage.test.ts` probes all 39 of them against this dispatcher)
 * instead of a shape a reader has to infer from a 1,100-line function.
 *
 * ORDER IS A CONTRACT IN EXACTLY ONE DIRECTION: a responder claiming a PREFIX
 * must return `null` for the siblings a later entry owns. Two live cases, and
 * both are stated where they live:
 *
 *   * `boxLifecycleMock` owns `/v1/runners/{releases,update}` and the
 *     per-box drain/owner/prompt, and hands BACK the roster, both enrollment
 *     transports and the revoke — which `fleetBoxesMock` catches below it.
 *   * `billingMock` owns the seat/spend half of `/v1/billing/*` and hands back
 *     `…/keys*` and `…/capacity` (`keysMock`) and `…/log-sink`
 *     (`accountConnectionsMock`).
 *
 * Nothing here claims `/v1/me` itself, which is why the two session doors in
 * `mockFetch` stay first: the signed-out story needs its 401 before any resource
 * is consulted.
 */
const RESOURCE_MOCKS: readonly MockResponder[] = [
  accountConnectionsMock,
  accountNotificationsMock,
  accountSettingsMock,
  accountTokensMock,
  accountWebhooksMock,
  adminAccessMock,
  // Ahead of `adminConsoleMock`, which answers every `/v1/admin/accounts/*` path.
  adminOperatorMock,
  adminConsoleMock,
  adminGoalsMock,
  adminGrantsMock,
  adminHiveMock,
  agentActivityMock,
  agentModesMock,
  agentQueueMock,
  agentsHistoryMock,
  agentsPreviewMock,
  approvalsMock,
  artifactsMock,
  auditFeedMock,
  billingMock,
  blockersMock,
  // Ahead of `boxLifecycleMock`, which rejects every `/v1/runners/:id/*` id outside its
  // own fixture: the Fleet scenarios' machines need a stats history (#6565).
  machineHistoryMock,
  // Ahead of `boxLifecycleMock` for the same reason: the scenario machines' moves (#6771).
  fleetMoveMock,
  boxLifecycleMock,
  ciMock,
  dashboardMock,
  donationsMock,
  emailStatusMock,
  fleetBoxesMock,
  fleetLiveMock,
  fleetTasksMock,
  fleetUtilizationMock,
  identitiesMock,
  kbMock,
  keyOutageMock,
  keysMock,
  llmRoutesMock,
  invitationAcceptMock,
  notificationPageMock,
  notificationSettingsMock,
  onboardingMock,
  orgMock,
  orgSsoMock,
  ossMock,
  overviewCountsMock,
  plansMock,
  promptVarsMock,
  repoEnablementMock,
  repoIssueMock,
  repoReadinessMock,
  repoWorkspaceMock,
  sessionsMock,
  squadsMock,
  systemPromptsMock,
  templatesMock,
  terminalMock,
];

/**
 * `?mock_slow_routes=<ms>`: hold every mock response for that many milliseconds.
 *
 * Under `PUBLIC_MOCKS=1` the read never leaves the page (`apiFetch` calls
 * `mockFetch` in-process), so Playwright's `page.route()` has no request to
 * delay and a loading state lasts a few microtasks. This door is how a story, a
 * screenshot or a layout-shift spec HOLDS a loading skeleton on screen long
 * enough to measure it. It is a latency knob, not a fixture: it delays whatever
 * the dispatcher below returns, answer or rejection alike, and changes neither.
 *
 * Absent door -> 0 (no delay, no extra tick). A present door that is not a whole
 * number of milliseconds is a REFUSAL naming the value, never "no delay": a typo
 * that silently became zero would let the loading spec pass against a page that
 * never loaded. It is a `UserFacingError` so the story gate quotes it.
 */
function slowRoutesMs(): number | UserFacingError {
  const raw = new URLSearchParams(window.location.search).get('mock_slow_routes');
  if (raw === null) return 0;
  if (/^\d+$/.test(raw)) return Number(raw);
  return new UserFacingError(
    `?mock_slow_routes must be a whole number of milliseconds, got ${JSON.stringify(raw)}`,
  );
}

/** Route a `/v1` request to its fixture, held by `?mock_slow_routes` when set. */
export function mockFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const delayMs = slowRoutesMs();
  if (delayMs instanceof UserFacingError) return Promise.reject(delayMs);
  const answer = answerMock<T>(path, init);
  if (delayMs === 0) return answer;
  // Settle the answer first so a rejection is held the same as a value: the
  // loading state is what is being stretched, not the error one.
  return answer.then(
    async (value) => {
      await sleep(delayMs);
      return value;
    },
    async (error: unknown) => {
      await sleep(delayMs);
      throw error;
    },
  );
}

/** The dispatcher proper. Unmapped paths reject (fail fast in dev). */
function answerMock<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';

  // #753: the signed-out Login stories need the auth gate's session probe to
  // fail. Dev-only door: any URL carrying ?mock_signed_out=1 rejects /v1/me
  // with the same 401 ApiError a real logged-out visit produces. Lazy import
  // reference (api.ts <-> mocks.ts cycle is benign: ApiError is only touched
  // at call time, and this whole module is tree-shaken out of prod builds).
  if (path === '/v1/me' && new URLSearchParams(window.location.search).has('mock_signed_out')) {
    return Promise.reject(new ApiError(401, 'signed out'));
  }
  // `?mock_invited=1`: the freshly invited person's first session (see
  // `meInvited`) — the tail of the access-request story, which then reads the
  // seats the invite granted off `billing-mocks.ts`'s half of the same door.
  if (path === '/v1/me' && new URLSearchParams(window.location.search).has('mock_invited')) {
    return Promise.resolve(meInvited as T);
  }
  // `?mock_member=1`: the same org as a MEMBER (see `meMember`); the settings
  // mock reads the same door for the role-aware half.
  if (path === '/v1/me' && new URLSearchParams(window.location.search).has('mock_member')) {
    return Promise.resolve(meMember as T);
  }
  if (path === '/v1/me') return Promise.resolve(me as T);

  for (const responder of RESOURCE_MOCKS) {
    const answered = responder(path, method, init);
    if (answered !== null) return answered as Promise<T>;
  }

  return unhandledMockRoute(method, path);
}
